
import AssistantOrderCard from '@/components/common/AssistantOrderCard.vue'
import AssistantDraftCard from '@/components/common/AssistantDraftCard.vue'
import { PLACEHOLDER_DATA_URI } from '@/assets/placeholder'
// 教程图片放大：复用 vant ImagePreview（与 /help/:slug 文章页同一个查看器，不为助手另造一套）
import { ImagePreview } from 'vant'
// 首页浮动入口共享 PEEK 状态机（货篮钮 Home/CartFloatEntry 与助手 FAB 同一套：同阈值、
// 同 class、同复原路径；视觉在 App.vue 全局 .float-fab）。本组件是「宿主形态 2」——
// 不依赖任何页面的滚动回调，用 mixin 自带的唯一一条 window scroll 监听（不写第二套）。
import floatingScrollBehavior from '@/mixins/floatingScrollBehavior'

// 悬浮助手壳：消息列表 / 文本输入 / 发送 / loading / 错误 / 清空会话。
// 单一壳按角色出能力面（GET /api/assistant/config）；对话 POST /chat（LLM 未配置时诚实降级为
// 商品搜索直调）；快捷查询 POST /call 直调只读工具。会话轻量：localStorage，不建 conversations 表。
// 商品搜索与正式搜索框同一搜索核心（同义词/拼音助记码/别名/编码召回），结果为结构化
// product_results：气泡内紧凑可点击行（查看商品 / 后台管理）+ 查看全部（带原 query 进正式搜索页）。
const MSG_CAP = 50
const PRODUCT_SHOW_CAP = 5 // 聊天内默认展示前 3~5 个，更多走"查看全部搜索结果"
// 拖拽几何常量（与 CSS .assistant-fab 尺寸同步）：
const FAB_SIZE = 48 // 玻璃钮边长（拖拽吸附/垂直安全区数学；六版 Liquid Glass 48px）
const FAB_EDGE_MARGIN = 20 // 吸边留白（与初始 right:20px 同源）

// 助手「入口可用性」会话级缓存（≠ LLM chat 可用性；生产 readiness gate §3 防半上线）：
// MCP_ENABLED=false 的环境 /api/assistant 整组 404 → FAB 整体隐藏（不能"点开就报错"）——
// 这是唯一判死条件。401=未登录不判死（登录后再探）；5xx/代理错误/断网不判死也不缓存，
// 3s 重试——后端重启窗口的 502/504 不再永久毒化整个标签页（2026-09-18 P0）。
let ASSISTANT_ALIVE = null

// FAB 首亮展开：进首页先短暂展开 [ ✦ 助手 ] 胶囊 ~1.8s 再收回方形 FAB——每个标签页
// 会话只做一次（HOME ONLY 离页重挂不重播，用户不会被打扰第二次）
let FAB_INTRO_DONE = false

export default {
  name: 'FloatingAssistant',
  // 五代交互（2026-09-25，用户拍板）：**回归货篮钮共享的滚动 PEEK 状态机**。
  // 三代（e6cf4ae，2026-09-20）曾整 mixin 摘除、滚动逻辑自持（fabHalf「吸附半隐藏、
  // 停住不复原、位移走 inline left」）；四代（17fcd7a，2026-09-22）只补了「上滑恢复」。
  // 五代把自持那套整体删掉，改回 mixin：首页两个浮动入口（货篮 / 助手）从此同一状态源、
  // 同一 class（is-peeking / peek-left）、同一视觉（App.vue .float-fab）、同一复原路径
  // （上滚 / 停 400ms / 点击）。**唯一有意保留的差异 = 点击语义**：货篮是一段式
  // （CartFloatEntry.onCartClick / Home.onCartClick：点击即复原并立即执行），助手是
  // **两段式**（半隐藏态首点只完整滑出，再点才开抽屉——用户 2026-09-25 明确要求，
  // 且是助手三代以来的既有交互，不是新发明的第三种机制）。见 onFabClick。
  mixins: [floatingScrollBehavior],
  // 宿主须提供 floatBtnRef（mixin 进 PEEK 时按按钮实际位置判定往哪侧收）——自定义组件
  // 选项，Vue 2 只并进 $options，mixin 内部走 this.$options.floatBtnRef 取
  floatBtnRef: 'fabBtn',
  components: { AssistantOrderCard, AssistantDraftCard },
  data () {
    return {
      open: false,
      loading: false,
      cooldown: false, // P0 §16：429 后 5s 冷却——期间禁发消息/快捷钮，绝不自动重试
      assistantAlive: ASSISTANT_ALIVE === true, // 后端 404（MCP 关）→ FAB 不渲染
      draft: '',
      messages: [],
      sessionId: '',
      productShowCap: PRODUCT_SHOW_CAP,
      config: null, // { chat_enabled, role, quick_actions }
      // FAB 自由拖拽（CartFloatEntry 同款模式：位移 >5px 视为拖拽不触发点击，松手吸边）
      isDragging: false,
      hasMoved: false,
      dragStartX: 0,
      dragStartY: 0,
      fabX: 0,
      fabY: 0,
      // 首亮展开态（2026-09-20）：true → FAB 呈 [ ✦ 助手 ] 胶囊，1.8s 后自动收回
      fabIntro: false,
      // 拖拽吸附边（只服务拖拽松手的吸边与垂直安全区；PEEK 的收边方向由 mixin 的
      // floatPeekSide 在进 PEEK 时按按钮实际位置重算，与此无关）
      fabEdge: 'right'
    }
  },
  computed: {
    isAdmin () {
      return this.$store.getters.isAdmin
    },
    // 各角色主页判定（route path 真值，审计自 router/index.js，勿凭猜测加路径）：
    //   user + operator → /home（Home；operator 无独立页面——router 原注释「登录后用统一
    //     /home /order /center，差异由组件注入」）
    //   admin / super_admin → /admin/dashboard（AdminDashboard，PC 后台首页；/admin 重定向至此）
    //     + /admin-mobile（AdminMobileDashboard「管理首页」）
    //     + /home（2026-09-18 用户定案：admin 移动端主页就是 /home 店铺首页——
    //       登录也落这里；此前矩阵漏了它，导致「移动端看不见、PC 后台看得见」）
    //   courier → /courier/orders（配送员唯一主页，requireCourier）
    // 订单/搜索/商品详情/专区/验货/设置/列表/表单等一切二级页 false。
    isAssistantHomeRoute () {
      const p = this.$route.path
      const role = this.$store.getters.currentUserRole
      if (role === 'admin' || role === 'super_admin') {
        return p === '/admin/dashboard' || p === '/admin-mobile' || p === '/home'
      }
      if (role === 'courier') {
        return p === '/courier/orders'
      }
      return p === '/home'
    },
    fabVisible () {
      // 登录态真值 = store.user（localStorage 恢复）；state.token 恒 null 不作依据
      if (!this.$store.getters.currentUserId) return false
      // 生产 MCP_ENABLED=false → /assistant 404 → 探针判死，FAB 不渲染（半上线防护）
      if (!this.assistantAlive) return false
      // HOME ONLY 统一规则（2026-09-18 收口）：删除「user home-only / staff 全站常驻」双分支，
      // 所有角色只在自己主页显示。v-if 卸载（非 CSS 隐藏）——离开主页整个子树（含 drawer）
      // 拆除，零 scroll/touch 监听残留（fabVisible watch 同步解绑滚动吸附监听）。
      return this.isAssistantHomeRoute
    },
    // 货篮钮是否占用悬浮位：占位 → 助手悬于其上；不占 → 助手落货篮位。
    // HOME ONLY 下助手只在 /home（user/operator）与货篮钮共存：页内货篮钮
    // （showCartFloat=cartCount>0）是唯一占位者；admin/courier 主页无货篮钮。
    cartFabVisible () {
      return this.$route.path === '/home' && (this.$store.state.cartCount || 0) > 0
    },
    quickActions () {
      return (this.config && this.config.quick_actions) || []
    },
    chatEnabled () {
      return !!(this.config && this.config.chat_enabled)
    },
    inputPlaceholder () {
      if (this.config == null) return '加载中…'
      return this.chatEnabled ? (this.isAdmin ? '问订单 / 商品 / 用户 / 概览…' : '找货、查订单、问规格…')
        : (this.isAdmin ? '搜商品（AI 对话未配置）…' : '搜商品（AI 对话未配置）…')
    }
  },
  watch: {
    messages: {
      handler () { this.persist() },
      deep: true
    },
    // 登录态出现即探针（App 挂载早于登录 → mounted 探不到；immediate 覆盖刷新即在线）
    '$store.getters.currentUserId': {
      immediate: true,
      handler (v) { if (v) this.probeAssistant() }
    },
    // HOME ONLY 配套：首页挂 PEEK 滚动监听，离页即解绑（mixin 的 unbind 顺带清态 →
    // 回页必为 VISIBLE，不把上一页的 PEEK 带回来）
    fabVisible (v) {
      v ? this.bindFloatScroll() : this.unbindFloatScroll()
      if (v) {
        this.startFabIntro() // 首亮展开（会话一次；mounted 覆盖刷新即在线场景）
      } else if (this.open) {
        // 开着抽屉离页 → 先收起（2026-09-25 修，实测缺陷）：HOME ONLY 的 v-if 挂在本组件**根元素**上，
        // 离页只是渲染子树被换掉，组件实例仍在 → beforeDestroy 的解锁兜底**不会触发**。结果是把
        // body{position:fixed;overflow:hidden} 这把锁留在**另一个页面**上：/order 被钉住不能滚动，
        // 而那页既没有抽屉也没有 FAB，用户无法自助解除（只能再回首页关一次）。收起面板＝解锁，
        // 与解绑监听同源处理。副作用：离页期间 open 归位，回首页是「收起态 + 完整 FAB」而非
        // 「抽屉还开着」（后者本来也解释不通——用户已经离开过那个页面）。
        this.closePanel()
      }
    }
  },
  mounted () {
    try {
      this.messages = JSON.parse(localStorage.getItem('assistant_messages') || '[]')
      if (!Array.isArray(this.messages)) this.messages = []
      // 旧会话消息可能无 products 字段——渲染层 v-if 兜底，无需迁移
    } catch (_) { this.messages = [] }
    this.sessionId = localStorage.getItem('assistant_session_id') || this.genSessionId()
    localStorage.setItem('assistant_session_id', this.sessionId)
    // 刷新即在线且已在首页：立即挂 PEEK 监听（路由切换场景由 fabVisible watch 兜住）
    if (this.fabVisible) this.bindFloatScroll()
    if (this.fabVisible) this.startFabIntro()
    // 视口变化（移动端地址栏收放/旋转）：垂直越安全区即回弹
    window.addEventListener('resize', this.onFabViewportChange)
  },
  created () {
    // confirmation_token 内存表（Phase 1 spec §17）：/call 建的草稿 token 只活在本页内存——
    // 刷新即弃（服务端可 resume 重签），绝不进 localStorage（消息持久化统一剥 token 字段）
    this._draftTokens = {}
  },
  beforeDestroy () {
    clearTimeout(this._probeRetryTimer)
    clearTimeout(this._introTimer)
    clearTimeout(this._fabIgnoreTimer)
    this.unbindFloatScroll() // mixin 的 beforeDestroy 也会兜一次（幂等）
    window.removeEventListener('resize', this.onFabViewportChange)
    this.unlockPageScroll() // drawer 开着被路由卸载（HOME ONLY v-if 拆除）时兜底解锁
  },
  methods: {
    genSessionId () {
      return 'as-' + (window.crypto && window.crypto.randomUUID
        ? window.crypto.randomUUID().slice(0, 18)
        : Date.now().toString(36) + Math.random().toString(36).slice(2, 10))
    },
    persist () {
      try {
        const capped = this.messages.slice(-MSG_CAP)
        if (capped.length !== this.messages.length) this.messages = capped
        // 持久化剥敏（Phase 1 spec §19）：任何 token 形态字段（confirmation_token 等）不落
        // localStorage——replacer 返回 undefined 即丢弃该键；内存 token 表本来就非序列化对象
        localStorage.setItem('assistant_messages', JSON.stringify(capped, (k, v) => (/token/i.test(k) ? undefined : v)))
      } catch (_) { /* 超限即弃旧，不影响使用 */ }
    },
    openPanel () {
      this.open = true
      this.lockPageScroll()
      // 抽屉打开期间退出 PEEK：先清态再解绑监听（规格：不允许 PEEK 动画在后面继续跑；
      // 解绑后页面已被 body 锁钉住，本来也没有用户滚动可听）
      this.resetFloatPeek()
      this.unbindFloatScroll()
      if (!this.config) this.fetchConfig()
      this.$nextTick(() => this.scrollBottom())
    },
    // ===== 背景滚动锁（2026-09-19）：drawer 打开期间钉住页面，关闭回到原位 =====
    // 方案：body position:fixed + top:-Ypx 补偿（iOS 背景滚动/橡皮筋根治方案，Quasar 无
    // QLayout 内滚容器——页面滚动真值在 window，见 floatingScrollBehavior 同源判定）。
    // 解锁还原样式后 scrollTo 原位，不跳顶。不挂任何全局 preventDefault——抽屉内部滚动
    // 靠 .assistant-body 自身 overflow（touchmove 不被吞），滚动链由 overscroll-behavior:
    // contain 阻断（mask 另加 touch-action:none：遮罩层本就无滚动手势需求）。
    lockPageScroll () {
      if (this._scrollLocked) return
      this._scrollLocked = true
      const y = window.pageYOffset || document.documentElement.scrollTop || 0
      this._lockY = y
      const bs = document.body.style
      bs.overflow = 'hidden'
      bs.position = 'fixed'
      bs.width = '100%'
      bs.top = (-y) + 'px'
    },
    unlockPageScroll () {
      if (!this._scrollLocked) return
      this._scrollLocked = false
      const bs = document.body.style
      bs.overflow = ''
      bs.position = ''
      bs.width = ''
      bs.top = ''
      // 复位 scrollTo 会产生 scroll 事件——滚动吸附监听只认用户滑动，程序性复位
      // 须豁免（短忽略窗盖住异步派发的 scroll 事件），否则关抽屉瞬间 FAB 又自我半隐藏
      this._fabIgnoreScroll = true
      clearTimeout(this._fabIgnoreTimer)
      this._fabIgnoreTimer = setTimeout(() => { this._fabIgnoreScroll = false }, 250)
      window.scrollTo(0, this._lockY || 0) // 回到打开前的位置，不跳顶
    },
    onFabClick () {
      this.cancelFabIntro() // 交互即收胶囊（下一行的拖拽/开抽屉都不带着展开态走）
      if (this.hasMoved) return // 拖拽松手不触发开面板
      // 两段式点击（用户 2026-09-25 明确锁死的交互，助手自三代起一直是这条；货篮是一段式，
      // 差异有意保留）：PEEK 态第一次点击只把 FAB 从半隐藏恢复完整（防误触——用户此刻大概率
      // 只是想命中那个半露的入口，不该一次动作同时完成「滑出 + 开抽屉」），完整态再点才开抽屉。
      // 必须先读 floatPeek 再 resetFloatPeek（后者会把标记清零）。
      const wasPeeking = this.floatPeek
      this.resetFloatPeek() // 点击即复原（共享状态机约定，与货篮 onCartClick 同款）
      if (wasPeeking) return
      this.openPanel()
    },
    // ===== 首亮展开（2026-09-20）：[ ✦ 助手 ] 胶囊 1.8s → 自动收回方形 FAB =====
    // 会话一次（FAB_INTRO_DONE）；点击/拖拽提前取消（cancelFabIntro）
    startFabIntro () {
      if (FAB_INTRO_DONE) return
      FAB_INTRO_DONE = true
      this.fabIntro = true
      clearTimeout(this._introTimer)
      this._introTimer = setTimeout(() => { this.fabIntro = false }, 1800)
    },
    cancelFabIntro () {
      clearTimeout(this._introTimer)
      this.fabIntro = false
    },
    // ===== 共享 PEEK 状态机（2026-09-25 五代）=====
    // 滚动判定 / 进 PEEK 条件 / 收边方向 / 复原路径全部来自 mixins/floatingScrollBehavior
    // ——与首页货篮钮（Home.handleScroll → updateFloatPeek）和全局货篮钮（CartFloatEntry
    // 的 bindFloatScroll）同一套常量与逻辑；视觉来自 App.vue 全局
    // .float-fab[.is-peeking][.peek-left]（半透明 0.55 + 向所在边收 55% 自身宽 + 20px）。
    // 本组件**不再自持第二条滚动监听**，也不再拿 inline left 做半隐藏（inline left/top
    // 从此只服务拖拽定位，与 PEEK 的 transform 互不干扰）。
    // 助手只补两条豁免——mixin 本身不知道「抽屉」和「拖拽」这两个组件内状态。
    updateFloatPeek (scrollTop) {
      // ① 抽屉开着：不参与 PEEK（规格：抽屉打开期间不允许 PEEK 动画在后面继续跑）。
      // ② 拖拽中：inline left 正跟着指针走，此时再叠一层 PEEK transform 会让钮跳位。
      // ③ 关抽屉的解锁复位：unlockPageScroll 会 scrollTo 回原位，那是程序性滚动而非用户
      //    下滚——不豁免的话关抽屉瞬间 FAB 又收边（旧自持实现同款豁免窗）。
      // 豁免期间把基线同步到实处：豁免结束后第一个真实滚动事件的方向不会判反。
      if (this.open || this.isDragging || this._fabIgnoreScroll) { this.$_floatLastY = scrollTop; return }
      return floatingScrollBehavior.methods.updateFloatPeek.call(this, scrollTop)
    },
    // 拖拽垂直安全区：上方避开 fixed header，下方避开底部导航（#app-nav）——FAB 不允许
    // 盖住 bottom nav（规格）。nav 滚动隐藏态 rect 是缩小后的几何，按其固定 bottom
    // （视口底 2px）+ 正常高度（5rem+2px，nav.scss 固定）还原真实上缘
    fabSafeLimits () {
      const winH = window.innerHeight
      let top = 12
      let bottom = winH - 12
      const nav = document.getElementById('app-nav')
      if (nav) {
        let navTop = nav.getBoundingClientRect().top
        if (nav.classList.contains('nav-hidden')) {
          const rem = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16
          navTop = nav.getBoundingClientRect().bottom - (rem * 5 + 2)
        }
        bottom = Math.min(bottom, navTop - 8)
      }
      const header = document.querySelector('.q-header, header.fixed, #app-header')
      if (header) {
        const hr = header.getBoundingClientRect()
        if (hr.height > 0 && hr.bottom > 0 && hr.bottom < winH / 2) top = Math.max(top, hr.bottom + 8)
      }
      return { top, bottom }
    },
    onFabViewportChange () {
      const btn = this.$refs.fabBtn
      if (!btn || !btn.style.left) return // 仍在 CSS 初始定位：bottom 变量自适应，无需接管
      // 隐藏态（抽屉开 = display:none，rect/offset 全 0）整体跳过：此刻重算垂直安全区
      // 会把 top 错 clamp 到屏顶 12px → FAB 停在屏顶「被位移」（真机复现：抽屉开期间点
      // 输入框弹键盘/地址栏收放都发 resize）。offsetWidth 兜底任何 display:none 形态。
      // 五代起 PEEK 偏移走 CSS transform（App.vue .float-fab），不再需要按新视口宽重算
      // inline left——这里只剩拖拽定位的垂直夹取
      if (this.open || !btn.offsetWidth) return
      const lim = this.fabSafeLimits()
      const y = btn.getBoundingClientRect().top
      if (y < lim.top || y > lim.bottom - FAB_SIZE) {
        btn.style.top = Math.max(lim.top, Math.min(lim.bottom - FAB_SIZE, y)) + 'px'
      }
    },
    // ===== FAB 拖拽（购物车悬浮钮同款：全屏移动 + 最近边缘吸附；drag end 完整态停留，
    // 不再自动半隐藏——下一次用户滚动才收）=====
    startFabDrag (e) {
      // 仅 mouse：阻止原生 drag 会话吞掉后续 mousemove。touchstart 上 preventDefault 会吞掉合成 click（手机上按钮点不动）；
      // touch 侧滚动由 CSS touch-action:none 兜底（购物车悬浮钮同款）
      if (e.type === 'mousedown' && e.preventDefault) e.preventDefault()
      this.cancelFabIntro() // 拖拽起点先收回胶囊（吸附数学按 48px 方形算）
      this.isDragging = true
      this.hasMoved = false
      const touch = e.touches ? e.touches[0] : e
      this.dragStartX = touch.clientX
      this.dragStartY = touch.clientY
      const btn = this.$refs.fabBtn
      if (!btn) return
      const rect = btn.getBoundingClientRect()
      this.fabX = rect.left
      this.fabY = rect.top
      document.addEventListener('mousemove', this.onFabDrag)
      document.addEventListener('mouseup', this.endFabDrag)
      document.addEventListener('touchmove', this.onFabDrag)
      document.addEventListener('touchend', this.endFabDrag)
    },
    onFabDrag (e) {
      if (!this.isDragging) return
      e.preventDefault()
      const touch = e.touches ? e.touches[0] : e
      const dx = touch.clientX - this.dragStartX
      const dy = touch.clientY - this.dragStartY
      const btn = this.$refs.fabBtn
      if (!btn) return
      // 位移超过阈值才算拖拽：此刻才关掉 left/top 过渡（跟随即时）；tap（无位移）
      // 不经过这里，transition 保持 CSS 表——reveal 动画不被 'none' 毒化
      if (!this.hasMoved && (Math.abs(dx) > 5 || Math.abs(dy) > 5)) {
        this.hasMoved = true
        btn.style.transition = 'none'
      }
      if (!this.hasMoved) return
      btn.style.left = (this.fabX + dx) + 'px'
      btn.style.top = (this.fabY + dy) + 'px'
      btn.style.right = 'auto'
      btn.style.bottom = 'auto'
    },
    endFabDrag () {
      if (!this.isDragging) return
      this.isDragging = false
      document.removeEventListener('mousemove', this.onFabDrag)
      document.removeEventListener('mouseup', this.endFabDrag)
      document.removeEventListener('touchmove', this.onFabDrag)
      document.removeEventListener('touchend', this.endFabDrag)
      const btn = this.$refs.fabBtn
      if (!btn) return
      // 无位移（tap）：不吸附不动状态——点击语义交给 onFabClick（半隐藏 tap 只展开）
      if (!this.hasMoved) return
      const rect = btn.getBoundingClientRect()
      const winW = window.innerWidth
      const lim = this.fabSafeLimits() // 垂直安全区：不盖 bottom nav / 避开 header
      // drag end 记边：按中心离哪侧近 snap 到对应边（规格）。PEEK 的收边方向不读这个
      // 值——mixin 在进 PEEK 时按按钮实际中心重算 floatPeekSide（拖到左半屏后下滚会
      // 收左缘，见 peek-left）。松手即完整态停留，下一次用户下滚才收
      this.fabEdge = (rect.left + rect.width / 2) < winW / 2 ? 'left' : 'right'
      // 清空 drag 期 'none' → 回落 CSS transition 表（left/top 0.2s ease-out，180~240ms
      // 区间）；不用 inline 重设——reduced-motion 媒体查询覆盖的是 CSS 表，回落才能接管
      btn.style.transition = ''
      btn.style.left = (this.fabEdge === 'left' ? FAB_EDGE_MARGIN : winW - FAB_EDGE_MARGIN - FAB_SIZE) + 'px'
      btn.style.top = Math.max(lim.top, Math.min(lim.bottom - FAB_SIZE, rect.top)) + 'px'
    },
    closePanel () {
      this.open = false
      this.unlockPageScroll() // 解除 body 锁并 scrollTo 回原位（带 250ms 程序性滚动豁免窗）
      // drawer 关闭 → 重新接入 PEEK 状态机，**按当前首页状态恢复、不无条件展开**：
      // 五代起不再调用 revealFab 那种「强制回到完整态」的 hard reset（那是三代那套自持
      // 实现的遗留）。这里只做一件事——重新绑定监听，基线对齐当前滚动位；FAB 呈现什么
      // 状态由状态机真值决定：PEEK 是「此刻正在下滚」的瞬时态（停 400ms 自动复原），
      // 抽屉开着期间页面被锁、用户不可能处于下滚中，所以关抽屉后的真值就是 VISIBLE；
      // 用户再下滚 → 立刻又进 PEEK（不是「恢复一次完整就再也不收」）。
      this.$nextTick(() => {
        if (this.fabVisible) this.bindFloatScroll()
      })
    },
    scrollBottom () {
      const el = this.$refs.body
      if (el) el.scrollTop = el.scrollHeight
    },
    // 移动键盘弹出时把输入区留在可视区（面板 70dvh，键盘占 visual viewport 时靠系统滚动兜底）
    keepInputVisible () {
      if (this.$refs.body && this.$refs.body.scrollIntoView) {
        try { this.$refs.body.scrollIntoView({ block: 'nearest' }) } catch (_) { /* 旧浏览器忽略 */ }
      }
    },
    pushMsg (role, text, products, orders, draft, helps) {
      this.messages.push({
        role, text: String(text == null ? '' : text),
        products: products || null, orders: orders || null, draft: draft || null,
        promos: null, helps: helps || null
      })
      this.$nextTick(() => this.scrollBottom())
    },
    // 富结果消息（草稿卡/活动/帮助块）：draft 所有状态键预置（Vue2 新增属性非响应——
    // 出生即全键，后续 state/order 变更才触发重渲染）
    pushRichMsg (text, draft, extra) {
      const d = draft ? Object.assign({ view: null, state: 'ready', order: null, idemKey: null, staleReason: null }, draft) : null
      const m = { role: 'assistant', text: String(text == null ? '' : text), products: null, orders: null, draft: d, promos: (extra && extra.promos) || null, helps: (extra && extra.helps) || null }
      this.messages.push(m)
      this.$nextTick(() => this.scrollBottom())
    },
    // ===== 帮助教程卡（§8/§13/§15）=====
    // 有步骤才渲染 <ol>——有些文章没有小节标题（extractSteps 退化为段落），硬套编号会误导
    hasSteps (helps) {
      return !!(helps && helps.answer && helps.answer.steps && helps.answer.steps.length)
    },
    // 其它相关教程：答案卡已讲最佳命中，列表里把同一 slug 去掉（同一篇不出现两次）
    otherHelps (helps) {
      if (!helps || !helps.items || helps.items.length < 2) return []
      const best = helps.answer && helps.answer.slug
      return helps.items.filter(h => h.slug !== best).slice(0, 3)
    },
    previewImage (url) {
      // 教程图片放大（§13：图片 = 缩略图 → 点击放大）；与文章页同款查看器
      if (url) ImagePreview({ images: [url], closeable: true, loop: false, zIndex: 7000 })
    },
    // 页面上下文（仅 UI 提示，不构成权限）：只带实际存在的字段
    pageContext () {
      const r = this.$route
      const ctx = { route: r.path, page_type: (r.meta && r.meta.title) || r.name || '' }
      if (r.params && r.params.id != null) {
        if (/^\/product/.test(r.path)) ctx.product_id = String(r.params.id)
        else if (/order/i.test(r.path)) ctx.order_id = String(r.params.id)
      }
      const kw = r.query && (r.query.keyword || r.query.q || r.query.search)
      if (kw) ctx.search_query = String(kw).slice(0, 100)
      return ctx
    },
    async fetchConfig () {
      try {
        const res = await this.axios.get('/assistant/config', { params: { session_id: this.sessionId } })
        this.config = res.data && res.data.data
      } catch (_) {
        this.config = { chat_enabled: false, role: this.$store.getters.currentUserRole, quick_actions: [] }
      }
    },
    // 入口可用性探针（assistantEntryAvailable 语义，≠ llmChatAvailable）：
    //   200 → 活（模块级缓存）；404 → 唯一判死（/assistant 整组不存在=MCP 关，不留死入口）；
    //   401 → 未登录不判死（登录态 watcher 再探）；
    //   其它（5xx/代理错误/断网）→ 不判死不缓存，3s 后重试。LLM/chat_enabled 与入口无关：
    //   探的是通道存在性，chat_enabled=false 只影响抽屉内自然语言对话（deterministic 工具照常）。
    async probeAssistant () {
      if (ASSISTANT_ALIVE !== null) { this.assistantAlive = ASSISTANT_ALIVE === true; return }
      if (this._probing) return
      this._probing = true
      let retry = false
      try {
        await this.axios.get('/assistant/config')
        ASSISTANT_ALIVE = true
      } catch (e) {
        const s = e && e.response && e.response.status
        if (s === 404) ASSISTANT_ALIVE = false
        else if (s !== 401) retry = true // 5xx/网络抖动：入口状态未知，稍后重试
      }
      this._probing = false
      if (retry) {
        clearTimeout(this._probeRetryTimer)
        this._probeRetryTimer = setTimeout(() => { this.probeAssistant() }, 3000)
        return // 保持 unknown，等重试落定
      }
      this.assistantAlive = ASSISTANT_ALIVE === true
    },
    async runQuick (qa) {
      // 确定性芯片（Phase 1 spec §21 中心矩阵）：路由/聚焦类不经 LLM 也不经工具，前端直接办
      if (qa.action === 'focus') {
        const el = this.$refs.input
        if (el && el.focus) el.focus()
        return
      }
      if (qa.action === 'route') {
        this.closePanel()
        this.$router.push(qa.route || '/home')
        return
      }
      await this.callTool(qa.tool, qa.input || {}, qa.label)
    },
    async callTool (tool, input, echoLabel) {
      if (this.loading) return
      this.loading = true
      if (echoLabel) this.pushMsg('user', echoLabel)
      try {
        const res = await this.axios.post('/assistant/call', { tool, input, session_id: this.sessionId })
        const fmt = this.formatResult(tool, res.data && res.data.data)
        if (fmt.draft || fmt.promos || fmt.helps) this.pushRichMsg(fmt.text, fmt.draft || null, fmt)
        else this.pushMsg('assistant', fmt.text, fmt.products || null, fmt.orders || null)
      } catch (e) {
        const status = e && e.response && e.response.status
        const body = e && e.response && e.response.data
        if (status === 429) this.startCooldown() // P0 §16：工具调用 429 同样冷却
        this.pushMsg('error', status === 429 ? '请求过于频繁，请稍后再试' : ((body && body.message) || '查询失败，请稍后再试'))
      } finally {
        this.loading = false
      }
    },
    // P0 §16：429 冷却 5s——发送钮/快捷钮禁用，杜绝短时间轰炸（服务端限流是真正边界，这只是 UX）
    startCooldown () {
      if (this.cooldown) return
      this.cooldown = true
      clearTimeout(this._cooldownTimer)
      this._cooldownTimer = setTimeout(() => { this.cooldown = false }, 5000)
    },
    async send () {
      const text = this.draft.trim()
      if (!text || this.loading || this.cooldown) return
      this.draft = ''
      // ===== Local Intent Router：订单查询走 direct path（spec 1 §11）=====
      // 明确订单号 / 我的订单 → 直接调订单工具，LLM calls = 0，卡片渲染也不耗 token；
      // 与 chatEnabled 无关（LLM 未配也能查订单）
      const orderNo = this.matchOrderNo(text)
      if (orderNo) {
        if (this.isAdmin) await this.callTool('admin_search_orders', { keyword: orderNo.slice(0, 50), page_size: 3 }, text)
        else await this.callTool('get_my_order_detail', { order_no: orderNo }, text)
        return
      }
      if (this.matchMyOrdersQuery(text)) {
        if (this.isAdmin) await this.callTool('admin_search_orders', { page_size: 3 }, text)
        else await this.callTool('get_my_orders', { page_size: 3 }, text)
        return
      }
      // 常买直查（2026-09-18）：真实历史订单聚合评分（服务端 0 LLM），结果走统一商品卡渲染器
      if (this.matchFrequentQuery(text)) {
        await this.callTool('get_my_frequent_products', { limit: 6 }, text)
        return
      }
      // 再来一单（Phase 1 spec §15/§21）：确定性直调——重建草稿等待确认，非 clone 直接下单；
      // 仅 user 角色有此芯片能力，staff 输入同词 → 工具层 403 诚实报错（不静默）
      if (!this.isAdmin && this.matchReorderQuery(text)) {
        await this.callTool('create_my_reorder_draft', {}, text)
        return
      }
      // 活动直查（spec §21）：确定性查询当前活动，不走 LLM
      if (this.matchPromotionsQuery(text)) {
        await this.callTool('get_active_promotions', {}, text)
        return
      }
      // 操作教程直查（§3）：确定性路径——步骤与教程视频在助手窗口内直接给，且不依赖 LLM 配置。
      // 放在订单/常买/再来一单/活动之后、找货与自然语言对话之前：那几条是更具体的业务意图，
      // 而"怎么下单"这类疑问句本来就落不到它们身上。
      const helpKw = this.matchHelpQuery(text)
      if (helpKw) {
        await this.callTool('search_help', { keyword: helpKw.slice(0, 50) }, text)
        return
      }
      if (this.chatEnabled) {
        this.loading = true
        this.pushMsg('user', text)
        try {
          const res = await this.axios.post('/assistant/chat', {
            message: text, session_id: this.sessionId, context: this.pageContext()
          })
          const d = res.data && res.data.data
          // draft_view（Phase 1 spec §20）：/chat 里整理的草稿由服务端持 token（会话路径确认），
          // 前端只拿 publicView 渲染确认卡——确认按钮走 /assistant/confirm（session_id 同源匹配）
          // 帮助教程（§3）：LLM 路径下 search_help 的步骤与媒体同样走结构化冷渲染——
          // 模型只出衔接语，教程内容不被它复述（说漏/说错都免了），也不会只剩一句"查看帮助中心"
          const helpExtra = d && d.help_results
            ? { answer: d.help_results.answer, items: d.help_results.items || [], total: d.help_results.total || 0 }
            : null
          if (d && d.draft_view) {
            this.pushRichMsg((d.assistant_text || d.error) || '采购单已备好，请确认：', { view: d.draft_view, state: d.draft_view.status === 'collecting' ? 'collecting' : 'ready' })
          } else {
            this.pushMsg('assistant', (d && (d.assistant_text || d.error)) || '（无回复）', (d && d.product_results) || null, (d && d.order_results) || null, null, helpExtra)
          }
        } catch (e) {
          const status = e && e.response && e.response.status
          const body = e && e.response && e.response.data
          if (status === 429) this.startCooldown() // P0 §16：冷却 + 明确文案，不自动重试
          this.pushMsg('error', status === 429 ? '请求过于频繁，请稍后再试' : ((body && body.message) || '助手暂时不可用'))
        } finally {
          this.loading = false
        }
      } else {
        // LLM 未配置：只有「明确 PRODUCT_SEARCH」才降级商品搜索直调（2026-09-18 收紧——
        // 此前任意文本都喂搜索，「你好/手机号/规格数字」类全部误触）；角色口径不同。
        // 无法确定意图 → 诚实提示「智能对话暂不可用」，给出可用出口，不猜
        const kw = this.matchProductSearchQuery(text)
        if (kw) {
          const tool = this.isAdmin ? 'admin_search_products' : 'search_products'
          await this.callTool(tool, { keyword: kw.slice(0, 50), page_size: 5 }, text)
        } else {
          this.pushMsg('error', '智能对话暂不可用（当前环境未配置 AI 对话服务）。可以：点下方快捷查询、粘贴订单号直查、输入「搜 + 商品名」找货')
        }
      }
    },
    async resetSession () {
      this.loading = true
      try { await this.axios.post('/assistant/reset', { session_id: this.sessionId }) } catch (_) { /* 服务端会话缺失不阻塞 */ }
      this.messages = []
      this.persist()
      this.loading = false
    },
    // ===== 商品结果跳转（复用系统真实路由，不猜路径） =====
    goProduct (productId) {
      // 与 Result.vue toDetails 同款：/details?id=；跳详情后助手收起（会话保留）
      this.closePanel()
      this.$router.push({ path: '/details', query: { id: productId } })
    },
    goAdminProduct (productId) {
      // PC 后台编辑详情页（Result.vue editProduct 同款）
      this.closePanel()
      this.$router.push('/product/' + productId + '/edit')
    },
    goAllResults (query) {
      // 带"原 query"进正式搜索结果页（与 Search.vue toResult 同款 {id}），复现同一搜索核心召回
      this.closePanel()
      this.$router.push({ path: '/result', query: { id: query || '' } })
    },
    // ===== 订单卡跳转（spec 1 §9/§10 + spec 2 §10：拉起系统现有订单详情，不新造页面） =====
    goOrderDetail (orderId) {
      // Assistant 面板先收（会话保留在 localStorage），订单页按 ?open= 拉起现有详情抽屉；
      // 关闭订单详情不自动重开 Assistant（§10）。Admin 宿主 → /admin/orders，其余 → /order
      this.closePanel()
      if (/^\/admin/.test(this.$route.path)) {
        this.$router.push({ path: '/admin/orders', query: { open: orderId } })
      } else {
        this.$router.push({ path: '/order', query: { open: orderId } })
      }
    },
    goOrderList () {
      this.closePanel()
      this.$router.push(/^\/admin/.test(this.$route.path) ? '/admin/orders' : '/order')
    },
    // ===== Local Intent Router 匹配器（订单号直查 / 我的订单 / 常买直查 / 找货判定） =====
    // 订单号真实格式 = YYYYMMDDHHMM(12位数字) + 4hex + 2校验hex = 18 字符（orderService.generateOrderNo，
    // rand/checksum 大小写 hex——单号常含字母；rand 段全数字时单号整体纯数字，仍是 18 位）。
    // 两形态按优先级（2026-09-18 收紧：去掉宽泛纯数字猜测——裸数字只认 18 位标准形态，
    // 其它数字必须带「订单/单号/查单」上下文；手机号/规格数字不再可能凭位数冒充单号）：
    //   1. 关键词相邻 token（订单/单号/查单 后 6~24 位，支持粘部分单号模糊查）
    //   2. 独立 18 位标准形态（12 数字 + 6 hex）——唯一接受的裸粘形态
    matchOrderNo (text) {
      const t = String(text || '')
      const kw = t.match(/(?:订单|单号|查单)[^0-9A-Za-z]{0,4}([0-9A-Za-z-]{6,24})/)
      if (kw) return kw[1]
      const shaped = t.match(/(?:^|[^0-9A-Za-z])(\d{12}[0-9A-Fa-f]{6})(?![0-9A-Za-z])/)
      if (shaped) return shaped[1]
      return null
    },
    matchMyOrdersQuery (text) {
      const t = String(text || '').trim()
      // 短指令形态（查订单/看订单/订单）或明确"我的/最近/列表"形态；含订单号的已在上一步分流
      if (/^(查|看|打开)?订单$/.test(t)) return true
      return /我的订单|最近.{0,2}的?订单|最新.{0,2}的?订单|订单列表|历史订单/.test(t)
    },
    // 常买直查（2026-09-18）：「我常买/常买/我常买的/经常买什么」——deterministic 命中，
    // 直接查真实历史订单聚合（0 LLM）；含其它关键词的长句不吞（留给搜索/对话）
    matchFrequentQuery (text) {
      const t = String(text || '').trim()
      if (/^(我|本人)?(经常|最|平时)?常买(的|什么|商品|东西|记录)?[?？!！。]?$/.test(t)) return true
      return /常买(什么|商品|东西)/.test(t)
    },
    // ===== 再来一单 / 活动匹配（Phase 1 spec §15/§21 确定性入口） =====
    // 「再来一单/再下一单/按上次那单再下」→ 直调重建草稿（服务端全量重验价/库存，等用户确认）
    matchReorderQuery (text) {
      const t = String(text || '').trim()
      if (/^再来一单[?？!！。]?$/.test(t)) return true
      return /按(上次|之前|最近)(的)?(那张)?(单|订单).{0,4}(再|重新)?(下|来|买|订)|再来一单/.test(t)
    },
    // 「活动/促销/有什么活动/现在有什么优惠」→ 直查当前活动（0 LLM）
    matchPromotionsQuery (text) {
      const t = String(text || '').trim()
      if (/^(活动|促销)[?？!！。]?$/.test(t)) return true
      return /(有|什么|看|查).{0,4}(活动|促销|优惠)/.test(t)
    },
    // ===== 操作教程判定（Help Assistant + Tutorial Media Phase §3 确定性入口） =====
    // 为什么要有：LLM 未配置时（chat_enabled=false）此前问"怎么下单"只会得到"智能对话暂不可用"
    // ——帮助问题是**确定性**的一类，不该依赖 LLM 才能答。命中即直调 search_help（0 LLM），
    // 步骤与教程视频照常渲染。返回关键词（喂给检索）或 null。
    // 判定口径（宁窄勿宽，避免吞掉真实找货意图）：
    //   ① 疑问词 + 操作主题词："怎么/如何/怎样 + 注册/下单/授权码/验货/配送/密码/…"
    //   ② 显式教程词："教程/操作指南/怎么操作/帮助文档"
    //   ③ 裸主题名词（2026-09-25 加宽）：用户直接敲"下单/授权码/验货"这类纯主题词
    // 不命中"我要下单 10 条轮胎"这类陈述句（无疑问词且非裸名词）——那仍走找货/下单路径。
    matchHelpQuery (text) {
      const t = String(text || '').trim()
      if (!t || t.length > 40) return null
      const TOPIC = /注册|账号|账户|登录|授权码|下单|采购|货篮|结账|结算|订单状态|订单进度|订单处理|订单管理|处理订单|管理订单|验货|配送|送达|收货|指派|派单|发货|改密|密码|手机号|忘记密码|找回|重置|搜索商品|找货|库存|规格/
      const ASK = /怎么|如何|怎样|咋|步骤|教程|操作指南|怎么操作|帮助/
      // 疑问词剥掉再测主题词（2026-09-25）："订单怎么处理"剥后 = "订单处理"。此前主题词表里
      // 只有"订单状态|订单进度"，"订单怎么处理"两半都测不中，用户的五个点名教程之一问不中帮助链。
      // 保留对原串的判断（并集），只加宽不收窄。
      if (ASK.test(t) && (TOPIC.test(t) || TOPIC.test(t.replace(ASK, '')))) return t
      if (/教程|操作指南|帮助文档/.test(t)) return t
      // 裸主题名词：此前"下单"会被 matchProductSearchQuery 的短词形状分支当**商品关键词**去搜货
      // （"下单"显然不是在找一个叫"下单"的商品）。只收多字实义主题词；三条刻意不收：
      //   · 裸「处理」——由疑问词分支覆盖，且"这个怎么处理"是不可确定意图（intent_gate 否定组）
      //   · 裸「订单」——matchMyOrdersQuery 已先接管，语义是"查我的单"而非"问流程"
      //   · 裸「手机号」「密码」——同否定组口径，更像账号问题而非教程
      const TOPIC_BARE = /^(注册|注册账号|授权码|下单|忘记密码|找回密码|订单处理|订单管理|处理订单|改密|修改密码|验货|配送)[?？!！。]?$/
      if (TOPIC_BARE.test(t)) return t
      return null
    },
    // ===== 明确 PRODUCT_SEARCH 判定（LLM 不可用时的降级口径，2026-09-18 收紧） =====
    // 此前 LLM 未配置时任意文本都降级商品搜索（「你好/手机号/规格数字」全误触）。
    // 现在只有词法上「明确找货」的输入才走 search_products，其余无法确定意图 →
    // 诚实提示「智能对话暂不可用」，不猜。返回搜索关键词或 null。三形态：
    //   1. 动词引导：搜/搜索/找/查一下/来点/要/买/换 + 宾语 → 取宾语（「搜机油」「帮我找个滤芯」）
    //   2. 问货问价：「XX 有货吗/有吗/多少钱/卖吗」 → 取主体（「机油多少钱」）
    //   3. 短商品词形状：≤20 字中/数/母名词（「机油」「滤」「2T机油」）——排除纯数字与规格
    //      数字形状（16*2.125 / 12.90-10 / 手机号）、问句形态（怎么/如何/？）、寒暄（你好/谢谢）、
    //      账号服务类词（手机号/密码/退款/客服…）
    matchProductSearchQuery (text) {
      const t = String(text || '').trim()
      if (!t || t.length > 30) return null
      const m = t.match(/^(?:请|麻烦|帮我|帮忙)?\s*(?:搜(?:索|一下|个)?|找(?:一下|个|点)?|查一?下|来[点份个一二]?|要[点份个一二]?|买[点份个一二]?|换[个条套一二]?)[一下]?\s*(.{1,20})$/)
      if (m && m[1].trim() && !/[?？]$/.test(m[1].trim())) return m[1].trim()
      const q = t.match(/^(.{1,20}?)\s*(?:有货(?:吗|没有|没)?|有(?:吗|没有|没)|多少钱|什么价|价格|卖吗|还在卖(?:吗)?|还有(?:吗|没))/)
      if (q && q[1].trim()) return q[1].trim()
      if (
        /^[一-鿿0-9A-Za-z][一-鿿0-9A-Za-z .*\-+/x×（）()]{0,19}$/.test(t) &&
        !/^[\d\s\-+.*\/x×.]+$/i.test(t) &&
        !/[?？]|怎么|如何|为什么|啥|什么/.test(t) &&
        !/^(你好|您好|嗨|哈喽|hi|hello|谢谢|多谢|再见|拜拜)/i.test(t) &&
        !/手机|电话|账号|密码|验证码|发票|退款|退货|物流|快递|单号|客服|投诉|优惠券|积分|会员|地址/.test(t)
      ) return t
      return null
    },
    // ===== 商品卡缩略图（统一渲染器配套）：字段真值来自商品 DTO（search items.image /
    // frequent items.image），$img 是正式 media resolver（myqcloud/相对路径同源规则同商品页）；
    // 无图/加载失败统一占位（PLACEHOLDER_DATA_URI，与商品卡系统同源）——识别用，不支持放大
    thumbOf (p) {
      const raw = p && (p.image || p.thumbnail)
      return (raw && this.$img(raw)) || PLACEHOLDER_DATA_URI
    },
    onThumbError (e) {
      e.target.src = PLACEHOLDER_DATA_URI
    },
    // ===== 采购单确认（Phase 1 spec §17：显式确认是用户事件，按钮 → /assistant/confirm） =====
    // 双路径对齐服务端：芯片建的草稿带内存 token；/chat 建的草稿不带（服务端 session 持有）。
    // 幂等键每次点击生成一次存消息上，重试/双击复用同一键 → 服务端只落一单。
    rememberToken (draftId, token) {
      if (!this._draftTokens) this._draftTokens = {}
      if (token) this._draftTokens[draftId] = token
      else delete this._draftTokens[draftId]
    },
    async onDraftConfirm (m) {
      if (this.loading || !m.draft || !m.draft.view) return
      const view = m.draft.view
      if (!m.draft.idemKey) {
        m.draft.idemKey = 'cf-' + (window.crypto && window.crypto.randomUUID
          ? window.crypto.randomUUID().slice(0, 22)
          : Date.now().toString(36) + Math.random().toString(36).slice(2, 10))
      }
      this.loading = true
      try {
        const body = { draft_id: view.draft_id, idempotency_key: m.draft.idemKey, session_id: this.sessionId }
        const tok = this._draftTokens && this._draftTokens[view.draft_id]
        if (tok) body.confirmation_token = tok
        const res = await this.axios.post('/assistant/confirm', body)
        const order = (res.data && res.data.data && res.data.data.order) || {}
        m.draft.state = 'done'
        m.draft.order = { id: order.order_id, order_no: order.order_no }
        m.text = order.idempotent ? '这张单已下过，订单号不变：' : '已下单，订单处理中：'
        this.rememberToken(view.draft_id, null) // 用完即弃（已确认草稿的 token 不可再用）
      } catch (e) {
        const status = e && e.response && e.response.status
        const rbody = e && e.response && e.response.data
        if (status === 429) this.startCooldown() // P0 §16：确认 429 同样冷却
        const reason = rbody && rbody.reason
        if (reason === 'DRAFT_EXPIRED' || reason === 'DRAFT_CHANGED') {
          // spec §18：过期/变更绝不静默重下单——卡置 stale 禁按钮，引导重新整理
          m.draft.state = 'stale'
          m.draft.staleReason = reason
          this.pushMsg('error', reason === 'DRAFT_EXPIRED' ? '采购单已过期（20 分钟有效），请重新整理' : '采购单已变更，请以最新一张为准')
        } else if (reason === 'PRODUCT_UNAVAILABLE') {
          this.pushMsg('error', (rbody && rbody.message) || '有商品已下架或库存不足，暂不能下单')
        } else {
          this.pushMsg('error', (rbody && rbody.message) || '确认失败，请稍后再试')
        }
      } finally {
        this.loading = false
      }
    },
    async onDraftCancel (m) {
      if (this.loading || !m.draft || !m.draft.view) return
      this.loading = true
      try {
        await this.axios.post('/assistant/call', {
          tool: 'abandon_my_order_draft',
          input: { draft_id: m.draft.view.draft_id },
          session_id: this.sessionId
        })
        m.draft.state = 'cancelled'
        this.rememberToken(m.draft.view.draft_id, null)
      } catch (e) {
        const rbody = e && e.response && e.response.data
        this.pushMsg('error', (rbody && rbody.message) || '取消失败，请稍后再试')
      } finally {
        this.loading = false
      }
    },
    // ===== 活动/帮助跳转（确定性路由，不猜路径） =====
    goPromotions () {
      // /promotion 是全局活动落地页（配置化 banner + 活动商品池），无单活动 id 参数
      this.closePanel()
      this.$router.push('/promotion')
    },
    goHelp (slug) {
      this.closePanel()
      this.$router.push(slug ? '/help/' + slug : '/help')
    },
    // 商品气泡永不报价（2026-09-17 Part2 补充）：DTO 不带价 → 这里无价格可显；
    // 价格统一走「查看商品」进正式详情页，由 canSeeProductPrice 既有规则决定
    // 工具结果 → 紧凑文本（气泡展示；商品搜索返回结构化 products 由模板渲染可点击行）
    formatResult (tool, d) {
      if (d == null) return { text: '（空结果）' }
      try {
        if (tool === 'search_products' || tool === 'admin_search_products') {
          const items = d.items || d.products || []
          const total = d.total || (d.pagination && d.pagination.total) || items.length
          const products = items.length ? { items, total, query: d.query } : null
          return {
            text: items.length ? `找到 ${total} 个相关商品：` : '没找到，换个关键词试试',
            products
          }
        }
        if (tool === 'admin_get_dashboard_summary') {
          return { text: `总订单 ${d.total_orders} · 总营收 ¥${d.total_revenue} · 今日新增 ${d.today_orders}\n待确认订单 ${d.pending_orders} · 待审核用户 ${d.pending_users}\n用户 ${d.total_users} · 商品 ${d.total_products}` }
        }
        if (tool === 'admin_search_orders' || tool === 'get_my_orders') {
          // 结构化订单卡（spec 1 §12 + spec 2 §11）：卡内已含商品明细/客户脱敏/履约方式，
          // 气泡只出一句计数；不再文本拼单号列表（那是被替换掉的旧形态）
          const cards = d.order_cards || []
          const total = tool === 'get_my_orders' ? (d.total != null ? d.total : cards.length) : ((d.pagination && d.pagination.total) != null ? d.pagination.total : cards.length)
          if (cards.length) {
            return { text: `找到 ${total} 个订单：`, orders: { type: tool === 'get_my_orders' ? 'order_results' : 'admin_order_results', items: cards.slice(0, 3), total } }
          }
          return { text: '没有找到符合条件的订单' }
        }
        if (tool === 'get_my_order_detail') {
          if (d.order_card) {
            return { text: '订单详情：', orders: { type: 'order_results', items: [d.order_card], total: 1 } }
          }
          return { text: '没有找到这个订单' }
        }
        if (tool === 'admin_get_order') {
          if (d.order_card) {
            return { text: '订单详情：', orders: { type: 'admin_order_results', items: [d.order_card], total: 1 } }
          }
          const o = d.order || {}
          const items = (d.items || []).map(i => `· ${i.product_name} ×${i.quantity}`)
          const tl = (d.timeline || []).slice(0, 5).map(t => `· ${t.time || ''} ${t.text || t.event_type || ''}`)
          return { text: `${o.order_no || o.id} · ${o.status_text || o.status}\n${items.join('\n')}` +
            (tl.length ? `\n—— 时间线 ——\n${tl.join('\n')}` : '') }
        }
        if (tool === 'admin_search_users') {
          const lines = (d.users || []).map(u => `· ${u.nickname || u.phone} · ${u.phone || ''} · ${u.role} · ${u.status}`)
          return { text: `找到 ${lines.length} 人\n` + (lines.join('\n') || '没找到') }
        }
        if (tool === 'admin_get_user') {
          return { text: `${d.nickname || d.phone} · ${d.role} · ${d.status}\n手机 ${d.phone || '—'} · 订单 ${d.order_count == null ? '—' : d.order_count} 单 · 消费 ¥${d.total_spent == null ? '—' : d.total_spent}\n地址 ${d.address || '—'}` }
        }
        if (tool === 'get_my_frequent_products') {
          // 统一商品卡（2026-09-18）：与搜索同一 product_results 渲染器（图/摘要/买过次数），
          // 不再拼「· 名称」纯文字列表——LLM 与前端都不做文本拼卡
          const items = (d.items || []).map(p => ({
            product_id: p.product_id,
            name: p.name,
            summary: p.summary || null,
            image: p.image || null,
            status: p.current_status,
            order_count: p.order_count
          }))
          return items.length
            ? { text: '根据你的历史订单，最常买这些：', products: { items, total: items.length, query: null } }
            : { text: '还没有常买记录，下单后会根据你的历史推荐' }
        }
        // ===== 草稿类工具（Phase 1 spec §15/§20）：结果 → 确认卡 =====
        // token 只进内存表（rememberToken），view 剥敏后再进消息（持久化二次兜底仍会剥 token 键）
        if (d.draft_id && ['create_my_order_draft', 'update_my_order_draft', 'resume_my_order_draft', 'create_my_reorder_draft'].includes(tool)) {
          if (d.confirmation_token) this.rememberToken(d.draft_id, d.confirmation_token)
          const view = Object.assign({}, d)
          delete view.confirmation_token
          const isReorder = tool === 'create_my_reorder_draft'
          const srcNote = isReorder && d.source_order && d.source_order.order_no ? `（按订单 ${d.source_order.order_no} 重新核价）` : ''
          return {
            text: (view.status === 'collecting' ? '采购单先建好了，还差收货信息：' : '采购单备好了，请确认：') + srcNote,
            draft: { view, state: view.status === 'collecting' ? 'collecting' : 'ready' }
          }
        }
        if (tool === 'get_active_promotions') {
          const items = d.items || []
          return items.length
            ? { text: `当前有 ${d.total || items.length} 个进行中的活动：`, promos: { items, total: d.total || items.length } }
            : { text: '当前没有进行中的活动' }
        }
        if (tool === 'search_help') {
          // 帮助教程（§3/§8）：不再只回"找到相关帮助文档：<标题>→ 点击跳转"——answer 里带着
          // 编号步骤与教程媒体，由模板在助手窗口内直接渲染（回答 + 视频就地播放）。
          const items = (d.items || []).slice(0, 5)
          const answer = d.answer || null
          if (answer) {
            return { text: answer.summary || `这样操作（来自《${answer.title}》）：`, helps: { answer, items, total: d.total || items.length } }
          }
          // 一条都没命中（§15）：如实说没找到 + 给帮助中心入口，不编流程。
          // text 留空——空态卡（.ash-empty）已经把这句话连同「打开帮助中心」渲染出来了，
          // 这里再写一遍会让同一句话在气泡里出现两次（E2E ⑫a 实测到的重复）。
          return { text: '', helps: { answer: null, items, total: d.total || items.length } }
        }
        const s = JSON.stringify(d)
        return { text: s.length > 600 ? s.slice(0, 600) + '…' : s }
      } catch (_) {
        return { text: JSON.stringify(d).slice(0, 300) }
      }
    }
  }
}
